<template>
  <v-container>
    <h1>Users</h1>
    <hr />

    <v-data-table
      :headers="[
        { text: '', value: 'actions', sortable: false },
        { text: 'Full Name', value: 'fullName' },
        { text: 'Email', value: 'email' },
        { text: 'Role', value: 'role' },
        { text: 'Has Subscribed', value: 'receiveResources' },
        { text: 'Has Consented', value: 'communicationConsent' },
        { text: 'Deals', value: 'dealCount' },
        { text: 'Reviews', value: 'reviewCount' },
      ]"
      :items="users"
      :loading="loading"
      loading-text="Loading... Please wait"
      item-key
      :footer-props="{
        'items-per-page-options': [2, 10, 20, 30, 40, 50],
        'show-first-last-page': true,
      }"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :server-items-length="totalItems"
      :sort-by.sync="sortBy"
      :search="search"
      :sort-desc.sync="sortDesc"
      :must-sort="true"
      class="users-table elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-toolbar-title>Users</v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-spacer />
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            class="p-4"
            solo
          />
          <button
            class="default-btn blue"
            @click="showCreateEditUserDialog">
            Create User
          </button>
        </v-toolbar>
      </template>

      <template #item.fullName="{ item, value }">
        <router-link
          class="default-btn link small"
          :to="{ name: 'AdminUser', params: { id: item.id }}"
        >{{ value }}</router-link>
      </template>

      <template #item.receiveResources="{ value }">
        {{ value ? 'Yes': 'No' }}
      </template>

      <template #item.communicationConsent="{ value }">
        {{ value ? 'Yes': 'No' }}
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="button-group">
          <button
            class="default-btn text text-blue minimal"
            @click="showCreateEditUserDialog(item)"
          ><v-icon color="inherit">mdi-pen</v-icon></button>

          <button
            class="default-btn text text-red minimal"
            @click="openDeleteDialog(item)"
          ><v-icon color="inherit">mdi-delete</v-icon></button>
        </div>
      </template>

      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Reset</v-btn>
      </template>
    </v-data-table>

    <DialogCreateEditUser
      v-if="createEditUserDialog.display"
      :originalUser="createEditUserDialog.originalUser"
      :editing="createEditUserDialog.editing"
      :organizations="organizations"
      @cancel="createEditUserDialog = false"
      @submit="save"
    />

    <v-dialog v-model="deleteUserDialog.display" max-width="450">
      <v-card>
        <v-card-title>
          <h2>Are you sure you want to delete this user?</h2>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="default-btn text" @click="onDeleteCancel">
            Cancel
          </v-btn>
          <v-btn class="default-btn red" @click="onDeleteConfirm">
            <span>Delete</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import DialogCreateEditUser from '@/modules/admin/components/DialogCreateEditUser'

export default {
  name: 'Users',
  components: {
    DialogCreateEditUser,
  },

  data: () => ({
    loading: false,
    organizations: [],
    users: [],

    createEditUserDialog: {},
    deleteUserDialog: {},

    showDeleteDialog: false,

    page: 0,
    itemsPerPage: 10,
    sortBy: 'id',
    sortDesc: true,
    search: '',
    totalItems: 0,
    // filterSortQuery: {},
  }),

  computed: {
    ...mapState('authStore', {
      userData: 'userData',
    }),
    ...mapGetters('feathers', ['apiOrganizations', 'apiUsers']),
  },

  watch: {
    page (newVal, oldVal) {
      if (newVal === oldVal) return
      this.getNewPage()
    },

    itemsPerPage (newVal, oldVal) {
      if (newVal === oldVal) return
      if (this.page === 1) {
        this.getNewPage()
      } else {
        this.page = 1
      }
    },

    sortBy (newVal, oldVal) {
      if (newVal === oldVal) return
      this.getNewPage()
    },

    sortDesc (newVal, oldVal) {
      if (newVal === oldVal) return
      this.getNewPage()
    },

    search (newVal, oldVal) {
      if (newVal === oldVal) return
      this.getNewPage()
    },
  },

  created () {
    this.initialize()
    this.page = 1
  },

  methods: {
    ...mapActions('authStore', ['setUserData']),

    async initialize () {
      this.loading = true

      try {
        this.organizations = await this.apiOrganizations.find({
          query: {
            $select: ['id', 'name'],
          },
        })
      } catch (error) {
        this.addAlert({
          type: 'error',
          messge: `Error retrieving organizations: ${error.message}`,
        })
      }

      this.loading = false
    },

    async getNewPage () {
      this.loading = true

      let query = {
        $limit: this.itemsPerPage || 10,
        $skip: (this.itemsPerPage || 10) * (this.page - 1)
      }
      if ( this.search.length > 0 ) {
        const keyword = "%" + this.search + "%";
        query = {...query,
          $or : {
            fullName: {$iLike: keyword},
            email: {$iLike: keyword}
          }
        }
      }
      this.filterSortQuery = {}

      this.filterSortQuery.$sort = {
        [this.sortBy]: this.sortDesc ? -1 : 1,
      }

      try {
        const results = await this.apiUsers.find({
          query: {
            ...query,
            ...this.filterSortQuery,
          },
        })

        this.users = results.data || []

        this.totalItems = results.total || 0
      } catch (error) {
        this.addErrorAlert(`Error retrieving users: ${error.message}`)
      }

      this.loading = false
    },

    showCreateEditUserDialog (user) {
      if (user) {
        const index = this.users.findIndex(u => u === user)

        if (index > -1) {
          this.createEditUserDialog = {
            display: true,
            editing: true,
            index,
            originalUser: user,
          }

          return
        }
      }

      this.createEditUserDialog = {
        display: true,
        editing: false,
      }
    },

    openDeleteDialog (item) {
      this.deleteUserDialog = {
        display: true,
        id: item.id,
      }
    },

    onDeleteCancel () {
      this.deleteUserDialog = {}
    },

    async onDeleteConfirm () {
      await this.onDeleteUser()
      this.deleteUserDialog = {}
    },

    async onDeleteUser () {
      if (this.deleteUserDialog.id) {
        try {
          await this.apiUsers.remove(this.deleteUserDialog.id)

          const index = this.users.findIndex(({ id }) => id === this.deleteUserDialog.id)
          this.users.splice(index, 1)

          this.addAlert({ message: 'the user was deleted successfully' })
        } catch (error) {
          this.addAlert({
            type: 'error',
            message: `There was an error deleting the user: ${error.message}`,
          })
        }
      }
    },

    async save (newUserData) {
      if (this.createEditUserDialog.editing) {
        try {
          const user = await this.apiUsers.patch(
            this.createEditUserDialog.originalUser.id,
            newUserData,
          )

          this.$set(this.users, this.createEditUserDialog.index, {
            ...user,
            dealCount: this.createEditUserDialog.originalUser.dealCount || 0,
            reviewCount: this.createEditUserDialog.originalUser.reviewCount || 0,
          })
          if (user.id === this.userData.id) this.setUserData(user)

          this.createEditUserDialog = {}
          this.addSuccessAlert('The user was updated successfully')
        } catch (error) {
          this.addErrorAlert(`There was an error updating the user: ${error.message}`)
        }
      } else {
        try {
          await this.apiUsers.create(newUserData)
          await this.getNewPage()

          this.createEditUserDialog = {}
          this.addSuccessAlert('The user was saved successfully')
        } catch (error) {
          this.addErrorAlert(`There was an error saving the user: ${error.message}`)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.users-table {
  width: 100%;
}

::v-deep {
  td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
